// AppErrorBoundary — the last line between a render error and a white screen.
//
// React unmounts the whole tree when a render throws and nothing catches it,
// so before this existed a single bad component anywhere in the app produced a
// blank page with an error only in the console. The user's read of that is
// "the site is broken", and they have no way back.
//
// It sits outside BrowserRouter in main.tsx, which is deliberate: if the crash
// came from the router, react-router's Link would crash again inside the
// fallback. Recovery is therefore a plain anchor and a full reload.

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { buttonClasses } from "@/components/ui/button";
import { SUPPORT_EMAIL } from "@/lib/legal";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Console only for now: there is no error-reporting service wired up, and
    // silently swallowing the stack would make these impossible to diagnose
    // from a user's screenshot.
    console.error("[error-boundary] render failed:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="grid min-h-full place-items-center px-6 py-16">
        <div className="w-full max-w-lg">
          <p className="font-mono text-sm text-[color:var(--color-accent-deep)]">
            Something broke
          </p>
          <h1 className="mt-2 font-display text-4xl tracking-tight">
            This page stopped halfway.
          </h1>
          <p className="mt-3 text-[color:var(--color-ink-soft)]">
            The error is on our side, not yours. Nothing you had saved is lost —
            reloading usually clears it.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={buttonClasses("primary", "md")}
            >
              Reload this page
            </button>
            <a href="/dashboard" className={buttonClasses("secondary", "md")}>
              Go to the dashboard
            </a>
          </div>

          {/* The message, not the stack: enough for a support email, not a
              wall of minified frames. */}
          <details className="mt-8 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-paper-tint)] px-4 py-3">
            <summary className="cursor-pointer text-sm font-medium text-[color:var(--color-ink-soft)]">
              Technical detail
            </summary>
            <p className="mt-2 break-words font-mono text-xs text-[color:var(--color-ink-mute)]">
              {error.message || String(error)}
            </p>
          </details>

          <p className="mt-6 text-sm text-[color:var(--color-ink-mute)]">
            If it keeps happening, send that detail to{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-[color:var(--color-accent-deep)] underline-offset-4 hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            and we'll look at it directly.
          </p>
        </div>
      </main>
    );
  }
}
